/**
 * Public user profile: stats across all games. Available for all plans.
 * Ads shown only when viewer's plan has showAds (Basic/Host see ads; Ad-free/Premium do not).
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlan } from '../contexts/PlanContext';
import { useGame } from '../contexts/GameContext';
import { GAMES } from '../constants/games';
import { getPlayerProfileData } from '../api/readApi';
import DataFetchOverlay from '../components/DataFetchOverlay';
import CachedAvatar from '../components/CachedAvatar';
import GoogleAd from '../components/GoogleAd';
import './UserProfile.css';

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showAds } = usePlan();
  const { games: enabledGames } = useGame();
  const [profile, setProfile] = useState(null);
  const [statsDoc, setStatsDoc] = useState(null);
  const [statsFromMatches, setStatsFromMatches] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPlayerProfileData(userId)
      .then((data) => {
        if (cancelled) return;
        setProfile(data?.profile ?? null);
        setStatsDoc(data?.statsDoc ?? null);
        setStatsFromMatches(data?.statsByGame ?? {});
      })
      .catch((err) => {
        if (!cancelled) {
          setProfile(null);
          setStatsDoc(null);
          setStatsFromMatches({});
          console.error('Error fetching user profile:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const displayName = profile?.displayName || profile?.email || 'Unknown';
  const avatarUrl = profile?.photoURL || profile?.avatarURL || '';

  const getStatsForGame = (gameId) => {
    const gameStats = statsDoc?.gameStats || {};
    const gs = gameStats[gameId];
    if (gs && ((gs.matchesWon ?? 0) + (gs.matchesLost ?? 0) > 0)) {
      const matchesWon = gs.matchesWon ?? 0;
      const matchesLost = gs.matchesLost ?? 0;
      const total = matchesWon + matchesLost;
      return {
        matchesWon,
        matchesLost,
        totalPoints: gs.totalPoints ?? 0,
        totalMatches: total,
        winRate: total > 0 ? ((matchesWon / total) * 100).toFixed(1) : '0',
      };
    }
    const fromMatches = statsFromMatches[gameId];
    if (fromMatches && fromMatches.matchesWon + fromMatches.matchesLost > 0) {
      const total = fromMatches.matchesWon + fromMatches.matchesLost;
      return {
        matchesWon: fromMatches.matchesWon,
        matchesLost: fromMatches.matchesLost,
        totalPoints: fromMatches.totalPoints ?? 0,
        totalMatches: total,
        winRate: total > 0 ? ((fromMatches.matchesWon / total) * 100).toFixed(1) : '0',
      };
    }
    return null;
  };

  // Only show games that are enabled (super admin config) and that the user has played
  const gamesToShow = Array.isArray(enabledGames) ? enabledGames : [];
  const gameRows = gamesToShow.map((game) => {
    const stats = getStatsForGame(game.id);
    return { game, stats };
  }).filter((r) => r.stats && r.stats.totalMatches > 0);

  const legacyTotal = statsDoc
    ? (statsDoc.matchesWon ?? 0) + (statsDoc.matchesLost ?? 0)
    : 0;
  // Only show legacy "All games" when all sports are enabled (no filtering); otherwise it could include disabled sports
  const allSportsEnabled = gamesToShow.length === GAMES.length;
  const legacyRow =
    legacyTotal > 0 && gameRows.length === 0 && allSportsEnabled
      ? {
          game: { id: '_all', name: 'All games', icon: '📊' },
          stats: {
            matchesWon: statsDoc.matchesWon ?? 0,
            matchesLost: statsDoc.matchesLost ?? 0,
            totalPoints: statsDoc.totalPoints ?? 0,
            totalMatches: legacyTotal,
            winRate: ((statsDoc.matchesWon ?? 0) / legacyTotal * 100).toFixed(1),
          },
        }
      : null;

  const rows = legacyRow ? [legacyRow] : gameRows;

  if (loading) {
    return <DataFetchOverlay message="Loading profile…" />;
  }

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        <div className="user-profile-header-row">
          <button onClick={() => navigate(-1)} className="btn-secondary btn-back">
            Back
          </button>
        </div>

        {showAds && <GoogleAd slotKey="BANNER" className="user-profile-ad" />}

        <div className="user-profile-card card-ui">
          <div className="user-profile-identity">
            <CachedAvatar
              url={avatarUrl}
              name={displayName}
              className="user-profile-avatar"
              alt={displayName}
            />
            <h1 className="user-profile-name">{displayName}</h1>
          </div>

          <h2 className="user-profile-stats-heading">Stats by game</h2>
          {rows.length === 0 ? (
            <p className="user-profile-no-stats">No match statistics yet.</p>
          ) : (
            <div className="user-profile-stats-table-wrap">
              <table className="user-profile-stats-table">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Wins</th>
                    <th>Losses</th>
                    <th>Matches</th>
                    <th>Points</th>
                    <th>Win rate</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ game, stats }) => (
                    <tr key={game.id}>
                      <td>
                        <span className="user-profile-game-name">
                          {game.icon} {game.name}
                        </span>
                      </td>
                      <td>{stats.matchesWon}</td>
                      <td>{stats.matchesLost}</td>
                      <td>{stats.totalMatches}</td>
                      <td>{stats.totalPoints}</td>
                      <td>{stats.winRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
